import React, { useEffect } from 'react';
import {
  Card,
  Grid,
  Header1Text,
  SpaceWrapper,
  PagerWrapper,
  PageLoading,
  BodyText,
  SpruceHelpInlineLink,
} from 'ui-toolkit';
import { getCountyForStateAction } from '../../store/counties';
import { OrdersSearchForm, OrdersTable } from '.';
import { getAllClientsAsyncAction } from 'store/clients/clientsSlice';
import { getAllLendersAsyncAction } from 'store/lenders/lendersSlice';
import { useAppSelector, useAppDispatch } from 'store/store';
import { useListTagsQuery } from '../../API/tags/TagApi';
import { useListPagedAdminUsersQuery } from '../../API/administration/AdminUsersAPI';
import { createDefaultPageData } from '../../types/rest/IPageData';
import { ErrorMessageTW } from '../../tailwindComponents/errorMessage/ErrorMessageTW';
import { eAdminAccountStatus } from '../../types/administration/userAdministration/IAdminUser';
import { useSearchOrdersQuery } from 'API/order/OrderApi';
import { convertToError } from 'API/BaseApi';
import { eUSAState } from '../../ui-toolkit/enums/states';
import { ISearchOrdersSpecificationData } from '../../ui-toolkit/orders/searchOrder/types/ISearchOrdersSpecification';
import { createDefaultSearchOrderResultData } from '../../ui-toolkit/orders/searchOrder/types/ISearchOrdersResult';

interface IOrdersProps {
  searchSettings: ISearchOrdersSpecificationData;
  setQueryObject: (value: ISearchOrdersSpecificationData) => void;
  onReset: () => void;
  queryObject: ISearchOrdersSpecificationData;
}

export const OrdersContainer: React.FC<IOrdersProps> = ({ searchSettings, setQueryObject, onReset, queryObject }) => {
  const { counties, countiesError } = useAppSelector((state) => state.counties);

  const { data: tags, isLoading: tagsIsLoading, error: tagsError } = useListTagsQuery();
  const { data: adminUsers = createDefaultPageData(), error: adminUsersError } = useListPagedAdminUsersQuery({
    fetchAll: true,
    status: eAdminAccountStatus.Active,
  });

  const {
    currentData: data = createDefaultSearchOrderResultData(),
    error,
    isFetching,
  } = useSearchOrdersQuery(queryObject);
  const fetchError = convertToError(error)?.message;

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getAllClientsAsyncAction());
    dispatch(getAllLendersAsyncAction());
  }, [dispatch]);

  const onStateChange = (state: eUSAState | null) => (state ? dispatch(getCountyForStateAction(state)) : {});

  const onPageChange = (pageNumber: number) => setQueryObject({ ...queryObject, pageNumber });
  const onPageSizeChange = (pageSize: number) => setQueryObject({ ...queryObject, pageSize });
  const onSearchOrder = (value: ISearchOrdersSpecificationData) => {
    setQueryObject(value);
  };

  const {
    clients: { data: clientData = [], fetchStatus: clientFetchStatus, fetchError: clientFetchError },
    lenders: { data: lenderData = [], fetchStatus: lenderFetchStatus, fetchError: lenderFetchError },
  } = useAppSelector((state) => state.usersCollection);
  const fetching = clientFetchStatus.fetching || lenderFetchStatus.fetching || tagsIsLoading;
  const clientLenderFetchError = clientFetchError || lenderFetchError || tagsError;
  const errorMessage = convertToError(clientLenderFetchError)?.message ?? '';

  return (
    <PageLoading isLoading={fetching}>
      {clientLenderFetchError && (
        <ErrorMessageTW>
          <BodyText>
            <code>{errorMessage}</code>
            <br />
            Could not get lenders & clients list.
            <br />
            Please <SpruceHelpInlineLink /> to submit a request for help.
          </BodyText>
        </ErrorMessageTW>
      )}

      <ErrorMessageTW rtkError={adminUsersError} />
      <SpaceWrapper>
        <Grid>
          <Header1Text>Orders</Header1Text>
          {fetchError || countiesError ? <ErrorMessageTW message={fetchError || countiesError} /> : null}
          <Card>
            <OrdersSearchForm
              adminUsers={adminUsers.content}
              lenders={lenderData}
              clients={clientData}
              initialValue={searchSettings}
              onSearchOrder={onSearchOrder}
              counties={counties}
              tags={tags || []}
              onStateSelect={onStateChange}
              onReset={onReset}
            />
          </Card>
          <Card>
            <Grid>
              <PagerWrapper
                total={data.totalElements}
                page={searchSettings.pageNumber}
                pageSize={searchSettings.pageSize}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                data-test-class={'orders-search'}
              >
                <OrdersTable
                  orders={data.content}
                  onSortChange={(sortBy) => onSearchOrder({ ...searchSettings, sortBy })}
                  sorting={searchSettings.sortBy}
                  loading={isFetching}
                />
              </PagerWrapper>
            </Grid>
          </Card>
        </Grid>
      </SpaceWrapper>
    </PageLoading>
  );
};
